import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import {
  GitHubRepoSelector,
  GitHubRepoUrlInput,
  StorageSizeInput,
  ServiceAccountInfo,
  SecretsInput,
  SecretsValidationField,
  SecretsModePicker,
  validateSecretsInput,
  validateSecretsValidationValue,
  PortFieldExtension,
} from './components/scaffolder';
import {
  DualEnvSecretsInput,
  DualEnvSecretsValidationField,
  validateDualEnvSecretsInput,
  DualEnvSecretEntry,
} from './components/scaffolder/secrets-input-dual-env';
import { ADOProjectSelector } from './components/scaffolder/ado-project-selector';

type GitHubRepoUrlFormValue = { value?: string; error?: string };

export const GitHubRepoSelectorExt = scaffolderPlugin.provide(
  createScaffolderFieldExtension<GitHubRepoUrlFormValue>({
    name: 'GitHubRepoSelector',
    component: GitHubRepoSelector,
    validation: async (formValue: { value?: string; error?: string }) => {
      if (formValue.error && formValue.error.trim() !== '') {
        throw new Error(formValue.error);
      }
    },
    schema: {
      uiOptions: {
        type: 'object',
        properties: {},
      },
      returnValue: {
        type: 'object',
        properties: {
          value: { type: 'string' },
          error: { type: 'string' },
        },
      },
    },
  }),
);

export const ADOProjectSelectorExt = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'ADOProjectSelector',
    component: ADOProjectSelector,
    schema: {
      uiOptions: {
        type: 'object',
        properties: {},
      },
      returnValue: {
        type: 'string',
        properties: {},
      },
    },
  }),
);

export const GitHubRepoUrlInputExt = scaffolderPlugin.provide(
  createScaffolderFieldExtension<GitHubRepoUrlFormValue>({
    name: 'GitHubRepoUrlInput',
    component: GitHubRepoUrlInput,
    validation: async (
      formValue: { value?: string; error?: string } | string,
    ) => {
      // Handle both string (legacy/redeploy) and object formats
      const urlValue =
        typeof formValue === 'string' ? formValue : formValue?.value;
      const errorValue =
        typeof formValue === 'string' ? undefined : formValue?.error;

      // Check if value is provided
      if (!urlValue || urlValue.trim() === '') {
        throw new Error('GitHub Repo URL is required');
      }
      // Check if there's an error (e.g., app already exists)
      if (errorValue && errorValue.trim() !== '') {
        throw new Error(errorValue);
      }
    },
    schema: {
      uiOptions: {
        type: 'object',
        properties: {},
      },
      returnValue: {
        type: 'object',
        properties: {
          value: { type: 'string' },
          error: { type: 'string' },
        },
      },
    },
  }),
);

export const StorageSizeInputExt = scaffolderPlugin.provide(
  createScaffolderFieldExtension<number>({
    name: 'StorageSizeInput',
    component: StorageSizeInput,
    schema: {
      uiOptions: {
        type: 'object',
        properties: {},
      },
      returnValue: {
        type: 'number',
      },
    },
  }),
);

export const ServiceAccountInfoExt = scaffolderPlugin.provide(
  createScaffolderFieldExtension<string>({
    name: 'ServiceAccountInfo',
    component: ServiceAccountInfo,
    schema: {
      uiOptions: {
        type: 'object',
        properties: {},
      },
      returnValue: {
        type: 'string',
      },
    },
  }),
);

type SecretEntry = { key: string; value: string };

export const SecretsInputExt = scaffolderPlugin.provide(
  createScaffolderFieldExtension<SecretEntry[]>({
    name: 'SecretsInput',
    component: SecretsInput,
    // Secondary defense-in-depth validator. The PRIMARY validation for the
    // empty-secrets case is handled by SecretsValidationFieldExt below,
    // because Backstage's scaffolder does NOT reliably invoke validators for
    // custom fields inside oneOf dependency branches (IDP-980).
    //
    // This secondary check fires when Backstage DOES call this validator
    // (i.e. when RJSF resolves the field into the active schema). The mount
    // flag ensures we skip validation when the component is hidden
    // (enableSecrets=false — RJSF bleed-through case).
    validation: validateSecretsInput,
    schema: {
      uiOptions: {
        type: 'object',
        properties: {},
      },
      returnValue: {
        type: 'array',
        items: {
          type: 'object',
          // Only expose 'key' in the review-step schema.
          // The 'value' field is intentionally omitted so that Backstage does
          // not render plaintext secret values on the scaffolder review page
          // (OWASP A02 - Sensitive Data Exposure). The value is still passed
          // through to the backend action handler at runtime.
          properties: {
            key: { type: 'string' },
          },
        },
      },
    },
  }),
);

/**
 * Primary validation guard for the "secrets" configuration block.
 *
 * Backstage's scaffolder does NOT invoke field-extension validators for custom
 * fields that live inside oneOf dependency branches (where the SecretsInput
 * field lives). This extension is registered for the top-level
 * `secretsValidation` field in the template, so Backstage WILL call its
 * validator on every NEXT/REVIEW click.
 *
 * The SecretsValidationField component emits:
 *   'INVALID' — enableSecrets=true + SkyStage Managed + no secrets added
 *   'OK'      — any other state (including when secrets are disabled)
 *
 * The validator throws when it receives 'INVALID', blocking step navigation
 * until the user adds at least one secret. See IDP-980.
 */
export const SecretsValidationFieldExt = scaffolderPlugin.provide(
  createScaffolderFieldExtension<string>({
    name: 'SecretsValidationField',
    component: SecretsValidationField,
    validation: validateSecretsValidationValue,
    schema: {
      uiOptions: {
        type: 'object',
        properties: {},
      },
      returnValue: {
        type: 'string',
      },
    },
  }),
);

/**
 * Conditionally renders the Secrets Type radio (SkyStage Managed vs BYOG).
 *
 * - New deployments: hidden, silently defaults to SkyStage Managed.
 * - Redeploys of SkyStage Managed apps: hidden, defaults to SkyStage Managed.
 * - Redeploys of existing BYOG apps: radio shown so the user can keep BYOG
 *   or switch to SkyStage Managed.
 *
 * See IDP-972.
 */
export const SecretsModePickerExt = scaffolderPlugin.provide(
  createScaffolderFieldExtension<string>({
    name: 'SecretsModePicker',
    component: SecretsModePicker,
    schema: {
      uiOptions: {
        type: 'object',
        properties: {},
      },
      returnValue: {
        type: 'string',
      },
    },
  }),
);

/**
 * Dual-environment secrets input for staging-first deployment model.
 *
 * Collects secret keys with separate values for staging and production.
 * The STAGING_ prefix in GCP Secret Manager is an internal implementation
 * detail hidden from users.
 */
export const DualEnvSecretsInputExt = scaffolderPlugin.provide(
  createScaffolderFieldExtension<DualEnvSecretEntry[]>({
    name: 'DualEnvSecretsInput',
    component: DualEnvSecretsInput,
    validation: validateDualEnvSecretsInput,
    schema: {
      uiOptions: {
        type: 'object',
        properties: {},
      },
      returnValue: {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            key: { type: 'string' },
            // stagingValue and productionValue intentionally omitted
            // to avoid exposing secrets on the review page
          },
        },
      },
    },
  }),
);

/**
 * Validation sentinel for dual-env secrets.
 */
export const DualEnvSecretsValidationFieldExt = scaffolderPlugin.provide(
  createScaffolderFieldExtension<string>({
    name: 'DualEnvSecretsValidationField',
    component: DualEnvSecretsValidationField,
    validation: validateSecretsValidationValue,
    schema: {
      uiOptions: {
        type: 'object',
        properties: {},
      },
      returnValue: {
        type: 'string',
      },
    },
  }),
);

export const PortFieldExt = scaffolderPlugin.provide(
  createScaffolderFieldExtension<number>({
    name: 'PortField',
    component: PortFieldExtension,
    validation: (value, validation) => {
      if (!value) return;
      if (typeof value !== 'number') {
        validation.addError('Port must be a number');
        return;
      }
      if (value < 1 || value > 65535) {
        validation.addError('Port must be between 1 and 65535');
        return;
      }
    },
    schema: {
      uiOptions: { type: 'object', properties: {} },
      returnValue: { type: 'number' },
    },
  }),
);
